import React, { useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import CopyableResponse from './CopyableResponse';

const Section = ({ title, children, defaultOpen = true, internal = false, danger = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className={`mb-3 ${internal ? 'cb-guidance' : ''} ${danger ? 'cb-dont' : ''}`}>
      <Card.Header className="p-0 bg-transparent border-0">
        <Button
          variant="link"
          className={`cb-section-toggle w-100 text-left text-dark d-flex justify-content-between align-items-center px-3 py-3 ${
            internal || danger ? 'text-decoration-none' : ''
          }`}
          onClick={() => setOpen(value => !value)}
        >
          <span>
            {(internal || danger) && (
              <span className="cb-internal-label d-block mb-1">{danger ? 'Do not say' : 'Internal guidance'}</span>
            )}
            <strong>{title}</strong>
          </span>
          {open ? <ChevronUp /> : <ChevronDown />}
        </Button>
      </Card.Header>
      {open && <Card.Body className="pt-0 px-3 pb-3">{children}</Card.Body>}
    </Card>
  );
};

const ConversationBankFlow = ({ record, placeholderContext, highlightBranchId, onCopied }) => {
  if (!record) return null;
  const branches = [...(record.branches || [])].sort((a, b) => a.branchOrder - b.branchOrder);

  const track = (responseType, branch = '') => {
    onCopied && onCopied({ record: record._id, branch, responseType });
  };

  return (
    <div>
      <div className="mb-3">
        <Badge variant="primary" className="mr-2">
          START
        </Badge>
        <span className="smallFont text-muted">{record.topicCategory}</span>
        {record.scenarioName && <div className="tinyFont text-muted mt-1">{record.scenarioName}</div>}
      </div>

      <Section title="1. Customer question or situation">
        <div className="cb-customer-words">{record.customerQuestionSituation}</div>
      </Section>

      <Section title="2. Starting response">
        <CopyableResponse
          text={record.startingResponse}
          placeholderContext={placeholderContext}
          onCopied={() => track('starting_response')}
        />
      </Section>

      <Section title="3. Possible customer replies">
        {branches.length === 0 && <div className="text-muted">No reply branches yet.</div>}
        {branches.map(branch => (
          <div
            id={`branch-${branch._id}`}
            key={branch._id}
            className={`rounded-border p-3 mb-3 ${
              String(highlightBranchId) === String(branch._id) ? 'border-info' : ''
            }`}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Badge variant="info">CONTINUE · Branch {branch.branchOrder}</Badge>
            </div>
            <div className="smallFont text-muted mb-1">Possible customer reply</div>
            <div className="cb-customer-words mb-3">{branch.possibleCustomerReply}</div>
            <div className="smallFont text-muted mb-1">Recommended next response</div>
            <CopyableResponse
              text={branch.recommendedResponse}
              placeholderContext={placeholderContext}
              onCopied={() => track('branch_response', branch._id)}
            />
          </div>
        ))}
      </Section>

      <Section title="4. Pitch-ready signal" internal>
        <p className="cb-response-text mb-0">{record.pitchReadySignal || 'No pitch-ready notes for this scenario.'}</p>
        <div className="tinyFont mt-2 mb-0">This is for the salesperson only. It is not copied to the customer.</div>
      </Section>

      <Section title="5. Customer summary">
        <CopyableResponse
          text={record.customerSummary}
          placeholderContext={placeholderContext}
          onCopied={() => track('customer_summary')}
        />
      </Section>

      <Section title="6. Bridge to pitch">
        <CopyableResponse
          text={record.pitchBridge}
          placeholderContext={placeholderContext}
          onCopied={() => track('pitch_bridge')}
        />
      </Section>

      <Section title="7. Relevant pitch">
        <div className="mb-2">
          <Badge variant="warning">PITCH</Badge>
        </div>
        <CopyableResponse
          text={record.relevantPitch}
          placeholderContext={placeholderContext}
          onCopied={() => track('relevant_pitch')}
        />
      </Section>

      <Section title="8. Appointment invitation">
        <div className="mb-2">
          <Badge variant="success">INVITE</Badge>
        </div>
        <CopyableResponse
          text={record.appointmentInvitation}
          placeholderContext={placeholderContext}
          onCopied={() => track('appointment_invitation')}
        />
      </Section>

      <Section title="9. Alternative next step">
        <CopyableResponse
          text={record.alternativeNextStep}
          placeholderContext={placeholderContext}
          onCopied={() => track('alternative_next_step')}
        />
      </Section>

      <Section title="10. Confirmation">
        <div className="mb-2">
          <Badge variant="dark">CONFIRM</Badge>
        </div>
        <CopyableResponse
          text={record.confirmationLanguage}
          placeholderContext={placeholderContext}
          onCopied={() => track('confirmation_language')}
        />
      </Section>

      <Section title="11. What not to do" danger>
        <p className="cb-response-text mb-0">{record.whatNotToDo || 'No exclusions recorded for this scenario.'}</p>
        <div className="tinyFont mt-2 mb-0">Internal only. There is no copy button on this section.</div>
      </Section>
    </div>
  );
};

export default ConversationBankFlow;
